/**
 * 動画ソースフィールドエディタ。
 * ソース（Firebase / YouTube / Vimeo）を切り替えながら videoUrl + videoSource を編集する。
 *
 * - Firebase: VideoGalleryDialog からファイル選択 / アップロード
 * - YouTube: URL 入力 + ID 抽出バリデーション + サムネイルプレビュー
 * - Vimeo: URL 入力 + ID 抽出バリデーション
 *
 * item 全体を受け取り（{ videoUrl, videoSource, ... }）、両フィールドをまとめて更新。
 */
import { Film, Video as VideoIcon, X, Youtube } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  extractVimeoId,
  extractYouTubeId,
  inferVideoSource,
  type VideoSource,
  youtubeThumbnail,
} from '@goenhub/landing-sections';

import { VideoGalleryDialog } from '@/components/common/VideoGalleryDialog';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';

interface Props {
  label: string;
  item: Record<string, unknown>;
  onItemChange: (next: Record<string, unknown>) => void;
}

export function VideoSourceFieldEditor({ label, item, onItemChange }: Props) {
  const videoUrl = typeof item.videoUrl === 'string' ? item.videoUrl : '';
  const storedSource = item.videoSource as VideoSource | null | undefined;
  const effectiveSource: VideoSource = storedSource ?? inferVideoSource(videoUrl);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [localUrl, setLocalUrl] = useState(videoUrl);

  useEffect(() => {
    setLocalUrl(videoUrl);
  }, [videoUrl]);

  const setSource = (source: VideoSource) => {
    if (source === effectiveSource) return;
    // ソース切替時は URL もリセット（ソース間で URL の意味が違うため）
    onItemChange({ ...item, videoSource: source, videoUrl: '' });
  };

  const setUrl = (url: string, source?: VideoSource) => {
    onItemChange({ ...item, videoUrl: url, videoSource: source ?? effectiveSource });
  };

  const youtubeId = effectiveSource === 'youtube' && videoUrl ? extractYouTubeId(videoUrl) : null;
  const vimeoId = effectiveSource === 'vimeo' && videoUrl ? extractVimeoId(videoUrl) : null;

  return (
    <div>
      <Label className="text-[11px]">{label}</Label>

      {/* ソース選択セグメント */}
      <div className="mt-1 inline-flex border border-[var(--border)] rounded-md overflow-hidden text-[11px]">
        <SourceTab
          active={effectiveSource === 'firebase'}
          onClick={() => setSource('firebase')}
          icon={<Film className="size-3" />}
          label="Firebase"
        />
        <SourceTab
          active={effectiveSource === 'youtube'}
          onClick={() => setSource('youtube')}
          icon={<Youtube className="size-3" />}
          label="YouTube"
        />
        <SourceTab
          active={effectiveSource === 'vimeo'}
          onClick={() => setSource('vimeo')}
          icon={<VideoIcon className="size-3" />}
          label="Vimeo"
        />
      </div>

      {/* ソース別の入力UI */}
      <div className="mt-2">
        {effectiveSource === 'firebase' && (
          <FirebaseInput
            videoUrl={videoUrl}
            onPick={() => setGalleryOpen(true)}
            onClear={() => setUrl('', 'firebase')}
          />
        )}
        {effectiveSource === 'youtube' && (
          <ExternalInput
            placeholder="https://www.youtube.com/watch?v=..."
            value={localUrl}
            onLocalChange={setLocalUrl}
            onCommit={(v) => setUrl(v, 'youtube')}
            valid={!!youtubeId}
            invalidHint="YouTube の URL から動画ID（11文字）を抽出できませんでした"
            previewUrl={youtubeId ? youtubeThumbnail(youtubeId) : null}
          />
        )}
        {effectiveSource === 'vimeo' && (
          <ExternalInput
            placeholder="https://vimeo.com/123456789"
            value={localUrl}
            onLocalChange={setLocalUrl}
            onCommit={(v) => setUrl(v, 'vimeo')}
            valid={!!vimeoId}
            invalidHint="Vimeo の URL から動画ID（数字）を抽出できませんでした"
            previewUrl={null}
          />
        )}
      </div>

      <VideoGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(url) => {
          setUrl(url, 'firebase');
          setGalleryOpen(false);
        }}
        title="動画を選択 / アップロード"
      />
    </div>
  );
}

function SourceTab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 transition-colors ${
        active
          ? 'bg-[var(--accent)] text-white'
          : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function FirebaseInput({
  videoUrl,
  onPick,
  onClear,
}: {
  videoUrl: string;
  onPick: () => void;
  onClear: () => void;
}) {
  if (videoUrl) {
    return (
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onPick}
          className="block w-32 aspect-video rounded border border-[var(--border)] overflow-hidden hover:opacity-80 transition-opacity flex-shrink-0 bg-black"
          title="動画を変更"
        >
          <video src={videoUrl} preload="metadata" muted playsInline className="w-full h-full object-cover" />
        </button>
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          <div className="text-[11px] text-[var(--text-muted)] truncate">
            {videoUrl.split('/').pop()?.split('?')[0] ?? videoUrl}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onPick}>
              <Film className="size-3 mr-1" />
              変更
            </Button>
            <Button size="sm" variant="ghost" onClick={onClear}>
              <X className="size-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onPick}
      className="w-full border border-dashed border-[var(--border)] rounded-md p-4 text-center hover:bg-[var(--bg-hover)] transition-colors"
    >
      <Film className="size-5 mx-auto text-[var(--text-muted)] mb-1" />
      <div className="text-xs text-[var(--text-muted)]">
        クリックして動画を選択 / アップロード
      </div>
    </button>
  );
}

function ExternalInput({
  placeholder,
  value,
  onLocalChange,
  onCommit,
  valid,
  invalidHint,
  previewUrl,
}: {
  placeholder: string;
  value: string;
  onLocalChange: (v: string) => void;
  onCommit: (v: string) => void;
  valid: boolean;
  invalidHint: string;
  previewUrl: string | null;
}) {
  return (
    <div className="space-y-1">
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onLocalChange(e.target.value)}
        onBlur={() => onCommit(value.trim())}
      />
      {value && !valid && (
        <div className="text-[10px] text-[var(--accent-red,#ef4444)]">{invalidHint}</div>
      )}
      {valid && previewUrl && (
        <img
          src={previewUrl}
          alt=""
          className="w-32 aspect-video object-cover rounded border border-[var(--border)]"
        />
      )}
    </div>
  );
}
